import './autocomplete.scss';

import { Spin } from 'antd';
import { forwardRef, useEffect, useMemo, useState } from 'react';

import { LoadingOutlined } from '@/media/antd-icons';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import { debounce } from '@/services/helpers/debounce';

import type { IInputProps } from '../input';
import Input from '../input';
import OutsideClickHandler from '../outside-click-handler';
import useAutoComplete from './useAutocomplete';

export const AUTOCOMPLETE_GROUP = 'autocomplete-group';

interface IProps extends Omit<IInputProps, 'onChange'> {
  options: IAutoCompleteOptions[];
  value: string;
  onChange: (value: string, option?: IAutoCompleteOption | undefined) => void;
}

const Autocomplete = forwardRef<HTMLInputElement, IProps>(
  ({ options: propsOptions, isFrozen, ...rest }, forwardedRef) => {
    const [topPosition, setTopPosition] = useState(false);
    const [focused, setFocused] = useState(false);
    const [focusTriggered, setFocusTriggered] = useState(false);

    const [lastValue, setLastValue] = useState('');

    const options: IAutoCompleteOption[] = useMemo(() => {
      return propsOptions.reduce((acc, option) => {
        if ('groupTitle' in option) {
          acc.push({ label: option.groupTitle, value: AUTOCOMPLETE_GROUP });
          acc.push(...option.options);
          return acc;
        }

        acc.push(option);
        return acc;
      }, [] as IAutoCompleteOption[]);
    }, [propsOptions]);
    const hasOptionGroups = propsOptions.some(
      (option) => 'groupTitle' in option
    );

    const handleChange = (
      value: string,
      option: IAutoCompleteOption | undefined
    ) => {
      rest.onChange(value, option);
    };

    const handleFocus = (value = '') => {
      setFocused(true);
      setLastValue(rest.value);
      rest.onChange(value);
    };

    const handleBlur = (value = '') => {
      setFocused(false);
      if (!rest.value && !value) rest.onChange(lastValue);
      setLastValue('');
    };

    const {
      bindInput,
      bindOptions,
      bindOption,
      isBusy,
      suggestions,
      selectedIndex,
      clearSuggestions
    } = useAutoComplete({
      value: rest.value,
      lastValue,
      onChange: handleChange,
      defaultOptions: options,
      delay: 200,
      source: (search) => {
        const newOptions = options.filter((option) => {
          if (option.value === AUTOCOMPLETE_GROUP) return true;
          const label = option.label.toLowerCase();
          return label.includes(search.toLowerCase());
        });
        if (newOptions[newOptions.length - 1]?.value === AUTOCOMPLETE_GROUP)
          newOptions.pop();

        return newOptions;
      },
      handleFocus,
      handleBlur
    });

    const handleScroll = useMemo(
      () =>
        debounce(() => {
          if (bindOptions.ref.current) {
            // Disable setting position when popup opened
            if (focused) return;

            const listTopPosition =
              bindOptions.ref.current.getBoundingClientRect().top;
            if (
              window.innerHeight - 260 < listTopPosition &&
              window.innerHeight - 260 > 260
            ) {
              setTopPosition(true);
            } else {
              setTopPosition(false);
            }
          }
        }, 300),
      [focused, bindOptions.ref]
    );

    useEffect(() => {
      handleScroll();
      const main = document.querySelector('main');
      main?.addEventListener('scroll', handleScroll);
      return () => {
        main?.removeEventListener('scroll', handleScroll);
      };
    }, [bindOptions.ref, handleScroll]);

    const handlePopupToggle = () => {
      if (isFrozen) return;

      if (focused) {
        handleBlur();
        clearSuggestions();
      } else {
        handleFocus();
      }
    };

    return (
      <OutsideClickHandler
        onOutsideClick={() => {
          if (!focused) return;
          clearSuggestions();
          handleBlur();
        }}
      >
        <div className="autocomplete">
          <Input
            ref={forwardedRef}
            {...rest}
            value={bindInput.value}
            placeholder={lastValue}
            endContent={
              <span
                className={`autocomplete__arrow ${
                  focused ? 'autocomplete__arrow--rotated' : ''
                }`}
              >
                <ChevronDownIcon />
              </span>
            }
            onChange={bindInput.onChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && focused) e.preventDefault();
              if (e.key === 'Tab') handleBlur();
              bindInput.onKeyDown(e);
              rest.onKeyDown?.(e);
            }}
            onFocus={(e) => {
              setFocusTriggered(true);
              handlePopupToggle();
              rest.onFocus?.(e);
            }}
            onClick={(e) => {
              setTimeout(() => {
                if (!focusTriggered) {
                  handlePopupToggle();
                }
                setFocusTriggered(false);
              }, 0);
              rest.onClick?.(e);
            }}
            error={!focused ? rest.error : undefined}
            isFrozen={isFrozen}
            autoComplete="off"
          />
          <div
            className={`autocomplete__popup ${
              focused ? 'autocomplete__popup--shown' : ''
            } ${topPosition ? 'autocomplete__popup--to-top' : ''}`}
          >
            <ul {...bindOptions} className="autocomplete__list">
              {isBusy ? (
                <div className="autocomplete__list-loading">
                  <Spin indicator={<LoadingOutlined />} size="large" />
                </div>
              ) : (
                suggestions.map((option, index) =>
                  option.value === AUTOCOMPLETE_GROUP ? (
                    <li
                      key={option.label}
                      style={{ padding: option.label ? undefined : '0' }}
                      className="autocomplete__group-item"
                    >
                      {option.label}
                    </li>
                  ) : (
                    <li
                      className={`autocomplete__item ${
                        selectedIndex === index
                          ? 'autocomplete__item--selected'
                          : ''
                      } ${hasOptionGroups ? 'autocomplete__item--with-group' : ''}`}
                      key={option.label + option.value}
                      {...bindOption}
                    >
                      {option.label}
                    </li>
                  )
                )
              )}
            </ul>
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
);

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;

export type IAutoCompleteOptions = IAutoCompleteOption | IAutoCompleteGroup;

export interface IAutoCompleteOption {
  value: string;
  label: string;
}

export interface IAutoCompleteGroup {
  groupTitle: string;
  options: IAutoCompleteOption[];
}
